import { useQuery } from '@apollo/client';
import { WelcomeGuide } from 'components/admin/header/WelcomeGuide';
import { SplitGuide } from 'components/admin/visual/SplitGuide';
import { TableView } from 'elements/admin/tableView/TableView';
import { TableViewHeader } from 'elements/admin/tableView/TableViewHeader';
import { TableViewHeaderCell } from 'elements/admin/tableView/TableViewHeaderCell';
import { TableViewRow } from 'elements/admin/tableView/TableViewRow';
import { TableViewRowCell } from 'elements/admin/tableView/TableViewRowCell';
import { IconButton } from 'elements/eventView/atomic/IconButton';
import { ReverseButton } from 'elements/eventView/atomic/ReverseButton';
import { CurrentUserForms } from 'graphql/query/__generated__/CurrentUserForms';
import { CurrentUserWorldMaps } from 'graphql/query/__generated__/CurrentUserWorldMaps';
import { GuidesForCurrentUserQuery } from 'graphql/query/__generated__/GuidesForCurrentUserQuery';
import { CURRENT_USER_GUIDES_QUERY, FORMS_CURRENT_USER_QUERY } from 'graphql/query/guideQuery';
import { WORLD_MAP_CURRENT_USER_QUERY } from 'graphql/query/worldQuery';
import { useFormMutations } from 'hooks/forms/useFormMutations';
import { useGuideCreateDefaults } from 'hooks/guide/useGuideCreateDefaults';
import { useGuideMutations } from 'hooks/guide/useGuideMutations';
import { useGuideShareLinks } from 'hooks/guide/useGuideShareLinks';
import { useLinkMapAndGuideToUser } from 'hooks/ownership/useLinkMapAndGuideToUser';
import { EColors2, EFonts } from 'libs/styles/styling';
import _ from 'lodash';
import { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GlobalContext } from 'session/state/context';
import { DEFAULT_THEME } from 'static/config/guideThemes';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

export const GuideLandingLogged = (props: Props): JSX.Element => {
    const { state } = useContext(GlobalContext);
    const guideMutations = useGuideMutations();
    const navigate = useNavigate();
    const formMutations = useFormMutations();
    const guideCreateDefaults = useGuideCreateDefaults();

    const guideForUserQuery = useQuery<GuidesForCurrentUserQuery>(CURRENT_USER_GUIDES_QUERY, {
        skip: !state.loggedUser,
        fetchPolicy: 'network-only'
    });
    const guideShareLinks = useGuideShareLinks();

    const mapsQuery = useQuery<CurrentUserWorldMaps>(WORLD_MAP_CURRENT_USER_QUERY);

    const formsQuery = useQuery<CurrentUserForms>(FORMS_CURRENT_USER_QUERY);

    const userMaps = mapsQuery.data?.currentUserWorldMaps ?? [];
    const userForms = formsQuery.data?.currentUserForms ?? [];

    useLinkMapAndGuideToUser();

    function getMapTitle(title: string) {
        return title !== '' ? title : Localization.UNNAMED_MAP;
    }

    function getGuideTitle(title: string) {
        return title !== '' ? title : 'Unnamed guide';
    }

    const userMapsNotInGuide = userMaps.filter((map) => !map.guide);

    return (
        <S_GuideLandingLogged className={props.className}>
            <WelcomeGuide
                title={Localization.LANDING_TITLE}
                description={Localization.LANDING_DESCRIPTION}
                items={[Localization.LANDING_ITEM1, Localization.LANDING_ITEM2]}
                color={DEFAULT_THEME.styling.matchColor}
                background={DEFAULT_THEME.headerImageUrl}
            />
            <SplitGuide
                // number={guideForUserQuery.data?.currentUserGuides?.length}
                // title={`${Localization.GUIDES}`}
                // description={Localization.GUIDES_DESC}
                buttons={[
                    {
                        text: Localization.CREATE_GUIDE,
                        iconName: 'plus',
                        onClick: () => {
                            guideMutations.create(guideCreateDefaults.Guide).then((result) => {
                                const guideId = result.data.g_createGuide.guide.guideId;
                                navigate(`/admin/${guideId}`);
                            });
                        }
                    },
                    {
                        text: Localization.CREATE_MAP,
                        iconName: 'plus',
                        onClick: () => {
                            guideMutations.create(guideCreateDefaults.Map).then((result) => {
                                const guideId = result.data.g_createGuide.guide.guideId;
                                navigate(`/admin/${guideId}`);
                            });
                        }
                    }
                ]}
            />
            {guideForUserQuery.data?.currentUserGuides?.length > 0 && (
                <S_Section $width={600}>
                    <TableView>
                        <TableViewHeader>
                            <TableViewHeaderCell>{Localization.NAME}</TableViewHeaderCell>
                            <TableViewHeaderCell>{Localization.TYPE}</TableViewHeaderCell>
                            <S_TableViewHeaderCell>{Localization.ACTIONS}</S_TableViewHeaderCell>
                        </TableViewHeader>
                        {guideForUserQuery.data?.currentUserGuides?.map((guide) => {
                            const previewLink = guideShareLinks.getEventViewLink(guide);
                            return (
                                <TableViewRow key={guide.guideId}>
                                    <TableViewRowCell>
                                        <S_TitleLinkList
                                            to={`/admin/${guide.guideId}`}
                                            target="_blank"
                                        >
                                            <S_Title>{getGuideTitle(guide.title)}</S_Title>
                                        </S_TitleLinkList>
                                    </TableViewRowCell>
                                    <TableViewRowCell>
                                        {(() => {
                                            if (guide.guideType === 'guide') {
                                                return Localization.GUIDE;
                                            }
                                            if (guide.guideType === 'map') {
                                                return Localization.MAP;
                                            }
                                            if (guide.guideType === 'old_guide') {
                                                return Localization.OLD_GUIDE;
                                            }
                                        })()}
                                    </TableViewRowCell>
                                    <S_TableViewRowCell>
                                        <IconButton
                                            iconName="pen"
                                            onClick={() => {
                                                window.open(`/admin/${guide.guideId}`, '_blank');
                                            }}
                                        />
                                        <IconButton
                                            iconName="eye"
                                            onClick={() => {
                                                window.open(previewLink, '_blank');
                                            }}
                                        />
                                    </S_TableViewRowCell>
                                </TableViewRow>
                            );
                        })}
                    </TableView>
                </S_Section>
            )}
            {userMapsNotInGuide?.length > 0 && (
                <SplitGuide
                    number={userMapsNotInGuide?.length}
                    title={`${Localization.INTERACTIVE_MAPS}`}
                    description={Localization.INTERACTIVE_MAPS_DESC}
                    button={{
                        text: Localization.CREATE_MAP,
                        iconName: 'plus',
                        onClick: () => {
                            navigate('/create');
                        }
                    }}
                />
            )}
            {userMapsNotInGuide?.length > 0 && (
                <S_Section>
                    <TableView>
                        <TableViewHeader>
                            <TableViewHeaderCell>{Localization.MAP_NAME}</TableViewHeaderCell>
                            <S_TableViewHeaderCell2>{Localization.ACTIONS}</S_TableViewHeaderCell2>
                        </TableViewHeader>
                        {_.orderBy(userMapsNotInGuide, 'timeUpdated', 'desc')?.map((map) => {
                            return (
                                <TableViewRow key={map.worldMapId}>
                                    <TableViewRowCell>
                                        <S_TitleLinkList
                                            to={`/minute/${map.worldMapId}`}
                                            target="_blank"
                                        >
                                            <S_Title>{getMapTitle(map.title)}</S_Title>
                                        </S_TitleLinkList>
                                    </TableViewRowCell>
                                    <S_TableViewRowCell2>
                                        <IconButton
                                            iconName="eye"
                                            onClick={() => {
                                                window.open(`/map/${map.worldMapId}`, '_blank');
                                            }}
                                        />
                                        <ReverseButton
                                            text={Localization.EDIT_MAP}
                                            iconName="pen"
                                            onClick={() => {
                                                navigate(`/minute/${map.worldMapId}`);
                                            }}
                                        />
                                    </S_TableViewRowCell2>
                                </TableViewRow>
                            );
                        })}
                    </TableView>
                </S_Section>
            )}

            <SplitGuide
                number={userForms.length}
                title={Localization.FORMS}
                description={Localization.FORMS_DESC}
                button={{
                    text: Localization.CREATE_FORM,
                    iconName: 'plus',
                    onClick: () => {
                        formMutations.create('New form').then((result) => {
                            const id = result.data.createForm.eventFormId;
                            navigate(`/form/${id}/edit`);
                        });
                    }
                }}
            />
            {userForms?.length > 0 && (
                <S_Section $width={600}>
                    <TableView>
                        <TableViewHeader>
                            <TableViewHeaderCell>{Localization.FORM_TITLE}</TableViewHeaderCell>
                            <TableViewHeaderCell>{Localization.ACTIONS}</TableViewHeaderCell>
                        </TableViewHeader>
                        {_.orderBy(userForms, 'title', 'asc')?.map((form) => {
                            return (
                                <TableViewRow key={form.eventFormId}>
                                    <TableViewRowCell>{form.title}</TableViewRowCell>
                                    <TableViewRowCell>
                                        <ReverseButton
                                            iconName="pencil"
                                            text={Localization.EDIT_FORM}
                                            onClick={() => {
                                                navigate(`/form/${form.eventFormId}/edit`);
                                            }}
                                        />
                                        <ReverseButton
                                            iconName="eye"
                                            text={Localization.FORM_SUBMISSIONS}
                                            onClick={() => {
                                                window.open(
                                                    `/form/${form.eventFormId}/submissions`,
                                                    '_blank'
                                                );
                                            }}
                                        />
                                    </TableViewRowCell>
                                </TableViewRow>
                            );
                        })}
                    </TableView>
                </S_Section>
            )}
        </S_GuideLandingLogged>
    );
};

const S_GuideLandingLogged = styled.div`
    display: flex;
    flex-direction: column;
    margin: 0 0 100px 0;
`;

const S_Section = styled.div<{ $width?: number }>`
    display: flex;
    flex-direction: row;
    width: ${(props) => props.$width ?? 800}px;
    align-self: center;
    padding: 30px 40px 10px 40px;
    border-radius: 45px;
    background-color: #eaf0f5;
`;

const S_TitleLinkList = styled(Link)`
    width: fit-content;
    &:hover {
        text-decoration: underline;
    }
`;

const S_Title = styled.div`
    color: ${EColors2.DARK};
    font-size: 16px;
    font-family: ${EFonts.Medium};
    width: fit-content;
`;

const S_TableViewHeaderCell = styled(TableViewHeaderCell)`
    &:nth-last-child(1) {
        width: 100px;
        flex: unset;
        text-align: center;
    }
`;

const S_TableViewRowCell = styled(TableViewRowCell)`
    &:nth-last-child(1) {
        width: 100px;
        flex: unset;
        justify-content: center;
    }
`;

const S_TableViewRowCell2 = styled(TableViewRowCell)`
    min-width: 430px;
    display: flex;
    flex-grow: 0;
    flex-shrink: 0;
    gap: 10px;
`;

const S_TableViewHeaderCell2 = styled(TableViewHeaderCell)`
    min-width: 430px;
    display: flex;
    flex-grow: 0;
    flex-shrink: 0;
    gap: 10px;
`;
